import { POSTAL_CODE_FORMAT } from "@shared/formats/postal-code";
import {
  getCountryFromRegion,
  getRegionCode,
  STATE_FORMAT,
  type CountryCode,
} from "@shared/formats/state";
import type { RfqLocationInput } from "./types";

export function normalizeRfqRegion(
  value: string | undefined,
): { state: string; country: CountryCode } | undefined {
  const state =
    STATE_FORMAT.normalize(value) ??
    (value ? getRegionCode(value.trim()) : undefined);
  const country = state ? getCountryFromRegion(state) : undefined;
  return state && country ? { state, country } : undefined;
}

export function isValidRfqState(
  value: string | undefined,
  country: string | undefined,
): boolean {
  return STATE_FORMAT.validate(value ?? "", resolveRfqCountry(value, country));
}

export function isValidRfqPostalCode(
  value: string | undefined,
  country: string | undefined,
  state?: string,
): boolean {
  const candidate = value?.trim().toUpperCase() ?? "";
  if (resolveRfqCountry(state, country) === "CA") {
    const normalized = POSTAL_CODE_FORMAT.normalize(candidate);
    return !!normalized && POSTAL_CODE_FORMAT.caPattern.test(normalized);
  }
  return (
    POSTAL_CODE_FORMAT.pattern.test(candidate) ||
    POSTAL_CODE_FORMAT.extendedPattern.test(candidate)
  );
}

export function resolveRfqCountry(
  state: string | undefined,
  country: string | undefined,
): CountryCode {
  const normalizedState = STATE_FORMAT.normalize(state);
  return (
    (normalizedState ? getCountryFromRegion(normalizedState) : undefined) ??
    (country === "CA" ? "CA" : "US")
  );
}

export function canonicalizeRfqPostalCode(
  value: string | undefined,
  country: string | undefined,
  state?: string,
): string {
  const candidate = value?.trim().toUpperCase() ?? "";
  if (resolveRfqCountry(state, country) !== "CA") return candidate;
  const normalized = POSTAL_CODE_FORMAT.normalize(candidate);
  return normalized && POSTAL_CODE_FORMAT.caPattern.test(normalized)
    ? normalized
    : candidate;
}

export function updateRfqLocationRegion(
  location: RfqLocationInput,
  state: string,
): RfqLocationInput {
  const country = resolveRfqCountry(state, location.address.country);
  return {
    ...location,
    address: {
      ...location.address,
      state,
      country,
      postalCode:
        country === location.address.country ? location.address.postalCode : "",
    },
  };
}
